import * as _ from 'underscore'

import { Enums } from '../common/enums'

import { DataProvider } from '../../helpers/data-provider'
import { Dictionary } from '../../helpers/dictionary'

import { ObjectCustomString } from '..//util/object-custom-string'
import { IOpData, IReferenceType, IOpChangeState, IOpObject, IJsonSerializable, ICopyable } from '../util/interfaces'

import { OpDataList } from '../util/op-data-list'
import { OpDistributor } from './op-distributor'
import { OpOperator } from './op-operator'
import { OpRouteData } from './op-route-data'
import { OpRouteDef } from './op-route-def'
import { OpRouteType } from './op-route-type'
import { OpRouteStatus } from './op-route-status'
import { OpTask } from './op-task'
import { Utils } from '../util/utils'
import { TypeHelper } from '../../helpers'


export class OpRoute implements ICopyable<OpRoute>, IJsonSerializable, IOpChangeState, IOpObject, IReferenceType {

    //#region Properties

    public idRoute: number
    public idRouteDef: number
    public year: number
    public month: number
    public week: number
    public idOperatorExecutor: number
    public idOperatorApproved: number
    public dateUploaded: Date
    public dateApproved: Date
    public dateFinished: Date
    public idRouteStatus: number
    public idRouteType: number
    public idDistributor: number
    public creationDate: Date
    public expirationDate: Date
    public name: string

    //#endregion


    //#region Navigation Properties

    public routeDef: OpRouteDef
    public routeType: OpRouteType
    public routeStatus: OpRouteStatus
    public operatorExecutor: OpOperator
    public operatorApproved: OpOperator
    public distributor: OpDistributor

    //#endregion

    //#region Custom Properties

    public taskList: OpTask[]
    public operatorList: OpOperator[]

    //#endregion


    //#region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpRoute) {
            this.copy(<OpRoute>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion


    //#region IOpChangeState implementation

    public opState: Enums.OpChangeState

    //#endregion


    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdRoute
    }
    public getReferenceKey(): number {
        return this.idRoute
    }
    public getReferenceValue(): any {
        return this
    }

    //#endregion


    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idRoute
    }

    public set idObject(idObject: any) {
        this.idRoute = idObject
    }

    public dataList: OpDataList<OpRouteData> = new OpDataList<OpRouteData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string

    //#endregion


    //#region IJsonSerializable Implementation

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj) {
            this.idRoute = opObj.ID_ROUTE
            this.idRouteDef = opObj.ID_ROUTE_DEF
            this.year = opObj.YEAR
            this.month = opObj.MONTH
            this.week = opObj.WEEK
            this.idOperatorExecutor = opObj.ID_OPERATOR_EXECUTOR
            this.idOperatorApproved = opObj.ID_OPERATOR_APPROVED
            this.dateUploaded = opObj.DATE_UPLOADED
            this.dateApproved = opObj.DATE_APPROVED
            this.dateFinished = opObj.DATE_FINISHED
            this.idRouteStatus = opObj.ID_ROUTE_STATUS
            this.idDistributor = opObj.ID_DISTRIBUTOR
            this.idRouteType = opObj.ID_ROUTE_TYPE
            this.creationDate = opObj.CREATION_DATE
            this.expirationDate = opObj.EXPIRATION_DATE
            this.name = opObj.NAME

            if (opObj._RouteDef)
                this.routeDef = new OpRouteDef(opObj._RouteDef)
            if (opObj._RouteType)
                this.routeType = new OpRouteType(opObj._RouteType)
            if (opObj._RouteStatus)
                this.routeStatus = new OpRouteStatus(opObj._RouteStatus)
            if (opObj._OperatorExecutor)
                this.operatorExecutor = new OpOperator(opObj._OperatorExecutor)
            if (opObj._OperatorApproved)
                this.operatorApproved = new OpOperator(opObj._OperatorApproved)
            if (opObj._Distributor)
                this.distributor = new OpDistributor(opObj._Distributor)

            if (opObj.TaskList)
                this.taskList = opObj.TaskList.map(t => new OpTask(t))
            if (opObj.OperatorList)
                this.operatorList = opObj.OperatorList.map(o => new OpOperator(o))

            if (opObj.DataList)
                this.dataList.push(...opObj.DataList.map(data => new OpRouteData(data)))
            else
                this.dataList = new OpDataList<OpRouteData>()

            this.dynamicValues = opObj.dynamicValues

            this.opState = opObj.OpState
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_ROUTE: this.idRoute,
            ID_ROUTE_DEF: this.idRouteDef,
            YEAR: this.year,
            MONTH: this.month,
            WEEK: this.week,
            ID_OPERATOR_EXECUTOR: this.idOperatorExecutor,
            ID_OPERATOR_APPROVED: this.idOperatorApproved,
            DATE_UPLOADED: this.dateUploaded,
            DATE_APPROVED: this.dateApproved,
            DATE_FINISHED: this.dateFinished,
            ID_ROUTE_STATUS: this.idRouteStatus,
            ID_DISTRIBUTOR: this.idRouteType,
            ID_ROUTE_TYPE: this.idRouteStatus,
            CREATION_DATE: this.creationDate,
            EXPIRATION_DATE: this.expirationDate,
            NAME: this.name,

            _OperatorExecutor: this.operatorExecutor != null ? this.operatorExecutor.toJSON(compressed) : null,

            DataList: this.dataList ? this.dataList.ToJSON() : null,
            TaskList: this.taskList != null ? _.map(this.taskList, (task: OpTask) => task.toJSON()) : null,
            OperatorList: this.operatorList != null ? _.map(this.operatorList, (operator: OpOperator) => operator.toJSON()) : null,

            OpState: this.opState
        }
    }

    //#endregion


    //#region ICopyable Implementation

    public copy(source: OpRoute, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return
        }

        if (includeNavigationProperties) {
            if (source.routeDef != null) {
                this.routeDef = source.routeDef
            }

            if (source.routeType != null) {
                this.routeType = source.routeType
            }

            if (source.routeStatus != null) {
                this.routeStatus = source.routeStatus
            }

            if (source.operatorExecutor != null) {
                this.operatorExecutor = source.operatorExecutor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (source.operatorApproved != null) {
                this.operatorApproved = source.operatorApproved.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }

            if (source.distributor != null) {
                this.distributor = source.distributor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
            }
        }

        if (includeCustomProperties) {
            if (source.taskList != null) {
                this.taskList = TypeHelper.clone(source.taskList)
            }

            if (source.operatorList != null) {
                this.operatorList = _.map(source.operatorList, (operator: OpOperator) => operator.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
            }
        }

        if (includeDataList && source.dataList != null) {
            this.dataList = source.dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeDynamicValues) {
            this.dynamicValues = TypeHelper.clone(source.dynamicValues, true)
        }

        this.idRoute = source.idRoute
        this.idRouteDef = source.idRouteDef
        this.year = source.year
        this.month = source.month
        this.week = source.week
        this.idOperatorExecutor = source.idOperatorExecutor
        this.idOperatorApproved = source.idOperatorApproved
        this.dateUploaded = source.dateUploaded
        this.dateApproved = TypeHelper.clone(source.dateApproved)
        this.dateFinished = TypeHelper.clone(source.dateFinished)
        this.idRouteStatus = source.idRouteStatus
        this.idDistributor = source.idDistributor
        this.idRouteType = source.idRouteType
        this.creationDate = source.creationDate
        this.expirationDate = source.expirationDate
        this.name = source.name
        this.opState = source.opState
    }

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpRoute {
        const newRoute = new OpRoute(null)

        newRoute.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newRoute
    }

    //#endregion


    //#region toString

    public toString(): string {
        const customFormatString: string = ObjectCustomString.getCustomStringFormat(this)

        if (customFormatString == null) {
            return this.name
        }

        return ObjectCustomString.getObjectCustomString(customFormatString, this)
    }

    //#endregion


    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpRoute[]): JQueryPromise<OpRoute[]> {
        return Utils
            .loadDataListNavigationProperties(dataProvider, list)
            .then(() => {
                return dataProvider.System
                    .getDictionaries([Enums.ReferenceType.IdRouteType, Enums.ReferenceType.IdRouteStatus])
                    .then((dicts: Dictionary<Dictionary<IReferenceType>>) => {
                        if (dicts != null) {
                            const routeTypeDict: Dictionary<OpRouteType> = dicts.tryGetValue(Enums.ReferenceType.IdRouteType) as Dictionary<OpRouteType>
                            const routeStatusDict: Dictionary<OpRouteStatus> = dicts.tryGetValue(Enums.ReferenceType.IdRouteStatus) as Dictionary<OpRouteStatus>

                            _.forEach(list, (route: OpRoute) => {
                                if (routeTypeDict != null) {
                                    route.routeType = routeTypeDict.tryGetValue(route.idRouteType)
                                }

                                if (routeStatusDict != null) {
                                    route.routeStatus = routeStatusDict.tryGetValue(route.idRouteStatus)
                                }
                            })
                        }

                        return list
                    })

            })
    }

    //#endregion


    //#region fromIOpObject

    public static fromIOpObject(object: IOpObject): OpRoute {
        const newObject: OpRoute = new OpRoute(null)

        newObject.idRoute = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpRouteData.fromIOpData(data)))

        return newObject
    }

    //#endregion

}